// React imports
import { useMemo } from 'react';

// Types
import AddOnService from '@spike/addon-service-model';
import { ValueTypeSelectorValue } from 'components/UI/V3/Forms/ValueTypeSelector';
import { TagOption } from 'components/UI/V3/TagsSelector/TagOption';
import Service from 'model/Service';
import { EnhancedProduct } from './useDiscountForm';

interface UseDiscountValidationParams {
    discountName: string;
    discountType: string;
    selectedClientTags: Array<TagOption<string>>;
    selectedGroomingServices: Service[];
    selectedDaycareServices: Service[];
    selectedBoardingServices: Service[];
    selectedTransportationServices: Service[];
    selectedEvaluationServices: Service[];
    selectedAddons: AddOnService[];
    selectedProducts: EnhancedProduct[];
    applyDiscountTo: string;
    discountValue: ValueTypeSelectorValue;
    tieredDiscountValues: number[];
}

interface UseDiscountValidationReturn {
    isFormValid: boolean;
    tierErrors: (string | null)[];
}

/**
 * validateTieredDiscounts - Internal Validation Helper
 *
 * Validates tiered discount values to prevent gaps in the discount structure.
 * Ensures that if a business offers discounts for 2nd and 4th pets, they must
 * also provide a discount for the 3rd pet (no zero-value gaps).
 *
 * @param {number[]} values - Array of tiered discount values
 * @returns {(string | null)[]} Array of error messages (null for valid tiers, string for errors)
 */
const validateTieredDiscounts = (values: number[]): (string | null)[] => {
    const errors: (string | null)[] = new Array(values.length).fill(null);

    // Find the first and last non-zero indices
    let firstNonZeroIndex = -1;
    let lastNonZeroIndex = -1;

    for (let i = 0; i < values.length; i++) {
        if (values[i] > 0) {
            if (firstNonZeroIndex === -1) {
                firstNonZeroIndex = i;
            }
            lastNonZeroIndex = i;
        }
    }

    // If there are no non-zero values or only one, no gaps to check
    if (firstNonZeroIndex === -1 || firstNonZeroIndex === lastNonZeroIndex) {
        return errors;
    }

    // Check for zero values between first and last non-zero values
    for (let i = firstNonZeroIndex + 1; i < lastNonZeroIndex; i++) {
        if (values[i] === 0) {
            errors[i] = 'Cannot be zero';
        }
    }

    return errors;
};

/**
 * useDiscountValidation - Form Validation Logic Hook
 *
 * Purpose:
 * Provides real-time validation for the discount form, ensuring all required
 * fields are filled correctly before allowing form submission.
 *
 * Responsibilities:
 * - Validates discount name is not empty
 * - Ensures discount type is selected
 * - Validates Client Tag discounts have at least one tag selected
 * - Checks that at least one service, add-on, or product is selected
 * - Validates "Apply Discount To" option is selected
 * - For simple discounts (Additional Pet/All Pets): Ensures value > 0
 * - For tiered discounts: Validates at least one tier has value > 0 and no gaps exist
 *
 * Key Features:
 * - Memoized validation to prevent unnecessary recalculations
 * - Separate tier-specific error tracking for inline field validation
 * - Comprehensive validation covering all form states
 * - Returns both overall form validity and field-specific errors
 *
 * Validation Rules:
 * - Required: Discount name, type, at least one item, discount application method
 * - Client Tag type: Must have tags selected
 * - Simple discounts: Value must be greater than zero
 * - Tiered discounts: No gaps between non-zero tiers, at least one tier > 0
 *
 * When to use:
 * Use this hook to enable/disable the Save button and display validation errors.
 * Pass all form state from useDiscountForm to get validation results.
 *
 * @param {UseDiscountValidationParams} params - All form state values to validate
 * @returns {UseDiscountValidationReturn} Object with isFormValid flag and tier-specific errors
 */
export const useDiscountValidation = (params: UseDiscountValidationParams): UseDiscountValidationReturn => {
    const {
        discountName,
        discountType,
        selectedClientTags,
        selectedGroomingServices,
        selectedDaycareServices,
        selectedBoardingServices,
        selectedTransportationServices,
        selectedEvaluationServices,
        selectedAddons,
        selectedProducts,
        applyDiscountTo,
        discountValue,
        tieredDiscountValues
    } = params;

    const tierErrors = useMemo(() => {
        if (applyDiscountTo === 'Tiered') {
            return validateTieredDiscounts(tieredDiscountValues);
        }
        return [];
    }, [applyDiscountTo, tieredDiscountValues]);

    const isFormValid = useMemo(() => {
        // Check if name is provided
        if (!discountName.trim()) return false;

        // Check if discount type is selected
        if (!discountType) return false;

        // If Client Tag type, must have at least one tag selected
        if (discountType === 'Client Tag' && selectedClientTags.length === 0) {
            return false;
        }

        // Check if at least one service is selected
        const hasSelectedServices =
            selectedGroomingServices.length > 0 ||
            selectedDaycareServices.length > 0 ||
            selectedBoardingServices.length > 0 ||
            selectedTransportationServices.length > 0 ||
            selectedEvaluationServices.length > 0 ||
            selectedAddons.length > 0 ||
            selectedProducts.length > 0;
        if (!hasSelectedServices) return false;

        // Check if apply discount to is selected
        if (!applyDiscountTo) return false;

        // Validate based on apply discount to selection
        if (applyDiscountTo === 'Additional Pet' || applyDiscountTo === 'All Pets') {
            return discountValue.value > 0;
        }

        if (applyDiscountTo === 'Tiered') {
            // At least one tiered discount must be filled and > 0
            const hasAtLeastOneNonZero = tieredDiscountValues.some(value => value > 0);
            // No gaps between non-zero tiers
            const hasNoGaps = tierErrors.every(error => error === null);
            return hasAtLeastOneNonZero && hasNoGaps;
        }

        return false;
    }, [
        discountName,
        discountType,
        selectedClientTags,
        selectedGroomingServices,
        selectedDaycareServices,
        selectedBoardingServices,
        selectedTransportationServices,
        selectedEvaluationServices,
        selectedAddons,
        selectedProducts,
        applyDiscountTo,
        discountValue,
        tieredDiscountValues,
        tierErrors
    ]);

    return { isFormValid, tierErrors };
};
